import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp, Close } from "@mui/icons-material";
import AiCreditsProgressBar from "../AiCreditsProgressBar/AiCreditsProgressBar";
import { useGitHubCopilotBudgetsModal } from "./useGitHubCopilotBudgetsModal";

interface GitHubCopilotBudgetsModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
}

const GitHubCopilotBudgetsModal: React.FC<GitHubCopilotBudgetsModalProps> = ({
  open,
  onClose,
  projectId,
}) => {
  const {
    members,
    loading,
    expandedRow,
    expandedData,
    budgetInputs,
    budgetErrors,
    savingMember,
    handleToggleRow,
    handleBudgetChange,
    handleSave,
  } = useGitHubCopilotBudgetsModal({ open, projectId });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography component="span" variant="h6">
          GitHub Copilot Budgets
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : members.length === 0 ? (
          <Alert severity="info">
            No users with GitHub Copilot access found in this project.
          </Alert>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={40} />
                  <TableCell>
                    <b>Name</b>
                  </TableCell>
                  <TableCell>
                    <b>Email</b>
                  </TableCell>
                  <TableCell align="right">
                    <b>Budget ($)</b>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <React.Fragment key={member.memberId}>
                    <TableRow
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleToggleRow(member.memberId)}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {expandedRow === member.memberId ? (
                            <KeyboardArrowUp />
                          ) : (
                            <KeyboardArrowDown />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell align="right">
                        ${member.budget.toFixed(2)}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        colSpan={4}
                        sx={{
                          py: 0,
                          borderBottom:
                            expandedRow === member.memberId
                              ? undefined
                              : "none",
                        }}
                      >
                        <Collapse
                          in={expandedRow === member.memberId}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ py: 2, px: 2 }}>
                            {expandedData[member.memberId]?.loading ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  py: 2,
                                }}
                              >
                                <CircularProgress size={24} />
                              </Box>
                            ) : (
                              <>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    mb: 2,
                                  }}
                                >
                                  <TextField
                                    label="Project Budget"
                                    type="text"
                                    size="small"
                                    value={budgetInputs[member.memberId] ?? "0"}
                                    onChange={(e) =>
                                      handleBudgetChange(
                                        member.memberId,
                                        e.target.value,
                                      )
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                    error={Boolean(budgetErrors[member.memberId])}
                                    helperText={budgetErrors[member.memberId]}
                                    inputProps={{
                                      inputMode: "decimal",
                                      step: "0.01",
                                    }}
                                    InputProps={{
                                      startAdornment: (
                                        <Box component="span" sx={{ mr: 0.5 }}>
                                          $
                                        </Box>
                                      ),
                                    }}
                                    sx={{ width: 160 }}
                                  />
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSave(member.memberId);
                                    }}
                                    disabled={
                                      savingMember === member.memberId ||
                                      Boolean(budgetErrors[member.memberId])
                                    }
                                  >
                                    {savingMember === member.memberId ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      "Save"
                                    )}
                                  </Button>
                                </Box>

                                {expandedData[member.memberId]?.budgets &&
                                  expandedData[member.memberId]?.usage && (
                                    <AiCreditsProgressBar
                                      baseline={
                                        expandedData[member.memberId].budgets!
                                          .baseline
                                      }
                                      projectBudgets={
                                        expandedData[member.memberId].budgets!
                                          .projectBudgets
                                      }
                                      currentUsage={
                                        expandedData[member.memberId].usage!
                                          .currentUsage
                                      }
                                    />
                                  )}
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GitHubCopilotBudgetsModal;
